import { Card, Flex, Tag } from 'antd'
import dayjs from 'dayjs'

import { GetConsultationResponse } from 'src/api/requests'
import { Text } from 'src/components'
import { useClassifierElements } from 'src/features/hiv-anamnesis/hooks/useClassifierElements'
import { useHivAnamnesisClassifiers } from 'src/features/hiv-anamnesis/hooks/useHivAnamnesisClassifiers'
import { aidsCategoryLetterOf } from 'src/features/hiv-anamnesis/utils/aidsCategory'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  data: GetConsultationResponse
}

export const DetailsFinalCard = ({ data }: Props) => {
  const { t, currentLang } = useLocales()

  const { styles } = useStyles()

  const { aidsClinicalCategoryId, aidsClinicalConditionsIdOf } =
    useHivAnamnesisClassifiers()
  const { elements: aidsCategories } = useClassifierElements(
    data.assessment?.aidsClinicalCategoryId ? aidsClinicalCategoryId : undefined
  )
  const aidsCategory = aidsCategories.find(
    (element) => element.id === data.assessment?.aidsClinicalCategoryId
  )

  const conditionIds = data.assessment?.aidsClinicalConditionIds ?? []
  const categoryLetter = aidsCategoryLetterOf(aidsCategory)
  const { elements: conditionElements } = useClassifierElements(
    conditionIds.length ? aidsClinicalConditionsIdOf(categoryLetter) : undefined
  )
  const conditions = conditionIds.map((id) => ({
    id,
    name: conditionElements.find((element) => element.id === id)?.translation?.[
      currentLang.value
    ]
  }))

  return (
    <Card>
      <Card title={t('consultations.final_diagnostic')}>
        <Flex vertical gap={24}>
          {data.assessment?.finalDiagnostics?.length && (
            <Flex vertical gap={8}>
              <Text weight={700}>{t('consultations.final_diagnostic')}</Text>

              <Flex gap={8} wrap="wrap">
                {data.assessment?.finalDiagnostics.map((diagnostic) => (
                  <Tag key={diagnostic.id} className={styles.tag}>
                    {diagnostic.translation?.[currentLang.value]}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          )}

          {data.assessment?.opportunisticDiseases?.length && (
            <Flex vertical gap={8}>
              <Text weight={700}>{t('treatments.opportunistic_diseases')}</Text>

              <Flex gap={8} wrap="wrap">
                {data.assessment.opportunisticDiseases.map((diagnostic) => (
                  <Tag key={diagnostic.id} className={styles.tag}>
                    {diagnostic.translation?.[currentLang.value]}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          )}

          {data.assessment?.dateOfDiseaseOnset && (
            <Flex vertical gap={8}>
              <Text weight={700}>
                {t('epidemiology.date_time_of_disease_onset')}
              </Text>

              <Text weight={400}>
                {dayjs(data.assessment.dateOfDiseaseOnset).format(
                  dayMonthYearHourMinutesFormatWithDot
                )}
              </Text>
            </Flex>
          )}

          {data.assessment?.aidsClinicalCategoryId && (
            <Flex vertical gap={8}>
              <Text weight={700}>
                {t('consultations.aids_clinical_category')}
              </Text>

              <Text weight={400}>
                {aidsCategory?.translation?.[currentLang.value] ?? '-'}
              </Text>
            </Flex>
          )}

          {categoryLetter && conditions.length > 0 && (
            <Flex vertical gap={8}>
              <Text weight={700}>
                {t('consultations.aids_clinical_conditions', {
                  category: categoryLetter.toUpperCase()
                })}
              </Text>

              <Flex gap={8} wrap="wrap">
                {conditions.map((condition) => (
                  <Tag key={condition.id} className={styles.tag}>
                    {condition.name ?? '-'}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          )}

          {data.assessment?.comments && (
            <Flex vertical gap={8}>
              <Text weight={700}>{t('form.notes')}</Text>

              <Text weight={400}>{data.assessment.comments}</Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </Card>
  )
}
